import React from "react";
import { LocalStorageService } from "../service/LocalStorageService";
import Helper from "../Helper";

export const PERMISSIONS = {
    APPOINTMENT_CREATE_EDIT: 1,
    ENTITLEMENT_VIEW: 2,
    APPOINTMENT_DELETE: 3,
    APPOINTMENT_VIEW_DELETE: 4,
    APPOINTMENT_PRINTER_BARCODE: 5,
    APPOINTMENT_SEND_BARCODE: 6,
    FIRM_USER_MANAGER: 8,
    FIRM_DOCTOR_MANAGER: 10,
    FIRM_INSTITUTION_MANAGER: 9,
    FIRM_OPERATION_MANAGER: 11,
    FIRM_UNIT_MANAGER: 12,
    APPOINTMENT_VIEW: 13,
    CASH_REGISTER_VIEW: 14,
    EXPENSE_CREATE_EDIT: 15,
    EXPENSE_DELETE: 16,
    EXPENSE_VIEW: 17,
    EXPENSE_VIEW_DELETE: 18,
    PAYMENT_CREATE_EDIT: 19,
    PAYMENT_DELETE: 20,
    PAYMENT_VIEW: 21,
    PAYMENT_VIEW_DELETE: 22,
    CASH_REGISTER_VIEW_DELETE: 23,
    DASHBOARD: 24,
    PAYMENT_EDIT_DATE: 25,
    APPLOG_VIEW: 26,
    FIRM_CASH_REGISTER: 27,
    EXPORT_CASH_VIEW: 32,
    EXPORT_APPOINTMENTS: 33,
    ENTITLEMENT_MANAGER: 34,
    ENTITLEMENT_CONFIRM: 35,
    ENTITLEMENT_UPDATE_APPOINTMENT_RESPONSIBLE_USER: 36,
    ENTITLEMENT_ACCESS_OTHER_RESPONSIBLE_USERS: 37,
    VIEW_PRICE: 38,
    FILTER_LOGS_BY_USER: 39,
    VIEW_ALL_INSTITUTIONS: 40,
    APPOINTMENT_ACCOUNTING: 41,
    EDIT_PAST_APPOINTMENT: 42,
    EDIT_PAST_PAYMENT: 43,
    VIEW_INSTITUTION_NOTE_RESPONSIBLE: 44,
    APPOINTMENT_VIEW_OTHER_USER: 46,
    ENTITLEMENT_RESPONSIBLE_FILTER: 47,
    ENTITLEMENT_EXPORT: 48,
    ENABIZ: 49,
    AI_CHAT: 50,
    ENABIZ_OPERATION_TRACKING: 51,
    NOTIFICATION_SETTINGS_MANAGER: 52
}

const UserActions = {
    SET_USER: 'SET_USER',
    SET_TOKEN: 'SET_TOKEN',
    SET_FIRM_SETTINGS: 'SET_FIRM_SETTINGS',
    SIGN_OUT: 'SIGN_OUT',
    SET_PRICE_LIST: 'SET_PRICE_LIST'
}

const defaultValue = {
    permissionIdList: [],
    role: {
        permissionIdList: [],
    },
    ...LocalStorageService.getUser({
        name: 'test',
        email: 'test'
    }),
    firmSettings: LocalStorageService.getFirmSettings({
        startTime: '08:00',
        endTime: '23:30',
        locale: 'tr-TR'
    }),
    token: LocalStorageService.getToken(),
    priceList: []
}

const UserContext = React.createContext(defaultValue);


function userReducer(state, action) {
    switch (action.type) {
        case UserActions.SET_USER: {
            LocalStorageService.setUser(action.data)

            return {
                ...state,
                ...action.data
            }
        }
        case UserActions.SET_PRICE_LIST: {
            return {
                ...state,
                priceList: action.data.priceList
            }
        }
        case UserActions.SET_FIRM_SETTINGS: {
            LocalStorageService.setFirmSettings(action.data)
            return {
                ...state,
                firmSettings: action.data
            }
        }
        case UserActions.SIGN_OUT: {
            LocalStorageService.setToken()
            LocalStorageService.setUser({})
            LocalStorageService.getFirmSettings({})

            return {
                data: {},
                token: null
            }
        }
        case UserActions.SET_TOKEN: {
            LocalStorageService.setToken(action.data)
            return {
                ...state,
                token: action.data
            }
        }
        default: {
            return state
        }
    }
}

function UserProvider({ children }) {
    const [user, dispatch] = React.useReducer(userReducer, defaultValue)

    const value = { user, dispatch }

    return (
        <UserContext.Provider value={value}>
            {children}
        </UserContext.Provider>
    )
}

function useUser() {
    const context = React.useContext(UserContext);

    if (!context)
        throw new Error('User Provider Error')

    const { user, dispatch } = context;

    const setToken = (token) => {
        dispatch({
            type: UserActions.SET_TOKEN,
            data: token
        })
    }

    const setUser = (user) => {
        dispatch({
            type: UserActions.SET_USER,
            data: user
        })
    }

    const setPriceList = (priceList) => {
        dispatch({
            type: UserActions.SET_PRICE_LIST,
            data: { priceList }
        })
    }

    const signOut = () => {
        dispatch({
            type: UserActions.SIGN_OUT
        })
    }

    const setFirmSettings = (settings) => {
        dispatch({
            type: UserActions.SET_FIRM_SETTINGS,
            data: settings
        })
    }

    const hasPermission = (permission) => {
        if (!user || !user.role || !user.role.permissionIdList)
            return false;

        return user.role.permissionIdList.includes(permission)
            || user.permissionIdList.includes(permission)
    }

    const isAuth = () => {
        return (user && user.token);
    }

    const formatPrice = (price, options = {}) => {
        if (!hasPermission(PERMISSIONS.VIEW_PRICE))
            return "-";

        return Helper.Currency.format(price, options);
    }

    return {
        user,
        priceList: user.priceList || [],
        firmSettings: user ? user.firmSettings : {},
        setToken,
        setUser,
        signOut,
        setFirmSettings,
        hasPermission,
        isAuth,
        setPriceList,
        formatPrice
    }
}

export { UserProvider, useUser }

